import {ProcessFlowchartConfigEditorHeaderConfig} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditorHeaderTypes';
import {
  LeftSidepanelContent,
  ProcessFlowchartMetadataEditorProps,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/ProcessFlowchartConfigEditorLeftSidepanel';
import {warn} from 'util/Logging';

import {
  ProcessFlowchartNodeAdapter,
  ProcessFlowchartTaskSchemaNodeAdapter,
} from '../flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {FlowchartValidator} from './FlowchartValidator';
import {PLUGIN_REGISTRY_INSTANCE} from './PluginRegistry';

// Define the metadata editor in terms of the component, the props will be passed in at render time to form the content
export interface ProcessesPluginMetadataEditorItem extends Omit<LeftSidepanelContent, 'content'> {
  editorComponent: React.ComponentType<ProcessFlowchartMetadataEditorProps>;
}

/**
 * Base class for application team extensions to the Processes editor.
 *
 * Application teams subclass this base type to specify the extensions they're making available for use
 * with the editor. The abstract properties on this type specify different kinds of extensions.
 *
 * Clients wishing to use the Processes editor (see ProcessFlowchartConfigEditor) with extensions should
 * import the plugins containing those extensions, instantiate them, and call registerForEditor() to
 * make extensions provided by those plugins available to the editor. It's best to do this outside
 * the React component using the editor. For example:
 *
 * ```tsx
 * const workflowsPlugin = new DefaultProcessesPlugin();
 * workflowsPlugin.registerForEditor();
 *
 * const myPlugin = new MyPlugin();
 * myPlugin.registerForEditor();
 *
 * function MyComponentWithEditor(props: MyComponentWithEditorProps): JSX.Element {
 *   // The editor will now have access to the extensions provided by the loaded plugins.
 *   return <ProcessFlowchartConfigEditor .../>;
 * }
 * ```
 *
 * See this confluence page for more information on plugins: https://benchling.atlassian.net/wiki/spaces/Workflows/pages/1285619729/Adding+A+Processes+Plugin
 *
 * Default plugins are registered during test setup in client/test/lib/testSetup.ts. If you are adding a new plugin, you may need to update test setup accordingly.
 */
export abstract class ProcessesPluginBase {
  /**
   * Unique identifier for this plugin. Used by the registry to avoid duplicate registration
   * and to deregister.
   */
  static readonly pluginName: string;
  get pluginName(): string {
    return (this.constructor as typeof ProcessesPluginBase).pluginName;
  }

  /**
   * Name of the flowchart type this plugin provides (e.g. "Procedure", "Workflow").
   * Flowchart type corresponds to a configurator that is used to configure the flowchart for a particular app domain.
   * Required if the plugin owns a flowchart type; omit if the plugin only adds node adapters
   * to an existing flowchart type (e.g. PROCEDURE_METHOD nodes in a Procedure flowchart).
   */
  abstract readonly flowchartTypeName?: string;

  /**
   * Node adapters this plugin provides. Can include:
   * - {@link ProcessFlowchartTaskSchemaNodeAdapter} for task execution types.
   * - {@link ProcessFlowchartNodeAdapter} for non–task-schema nodes (e.g. Router, End).
   * The registry looks up adapters by execution type (task nodes) or node type (e.g. ROUTER_NODE).
   * Each execution type or node type must have exactly one adapter across all registered plugins.
   * Node adapters tell the editor how to work with nodes for a particular task type, e.g. how to render
   * the node on the flowchart canvas and what sidepanel configuration component to show for the node.
   */
  abstract readonly nodeAdapters: Array<ProcessFlowchartNodeAdapter>;

  /**
   * Validator for flowchart-level rules (e.g. data flow, chained routers, app-specific constraints).
   * Invoked during validation passes in validateProcessFlowchart; runs in addition to
   * per-node-type validation from adapters. Must implement validateFlowchartForType,
   * errorTypeToMessageOverride, and healFlowchart. See {@link FlowchartValidator}.
   */
  abstract readonly flowchartValidator: FlowchartValidator<object, string, string>;

  /**
   * Optional component for editing flowchart-level metadata (e.g. default folder, instructions).
   * When provided, the editor shows it in the left sidepanel for the flowchart type delivered
   * by this plugin. Omit if the flowchart type has no metadata to edit.
   */
  abstract readonly metadataEditor?: ProcessesPluginMetadataEditorItem;

  /**
   * The static header configuration for the flowchart editor.
   *
   * Override this property to provide header configuration for the flowchart type.
   * Dynamic overrides (breadcrumb URL, flowchart name) are applied separately via
   * HeaderConfigMetadata passed to the header component.
   */
  readonly headerConfig?: ProcessFlowchartConfigEditorHeaderConfig;

  /**
   * Registers this plugin with the global registry so the flowchart config editor can
   * use its adapters, flowchart-level validator, metadata editor, header configuration,
   * and flowchart type mapping. Call once at app or test setup (e.g. before rendering
   * the editor). Do not override in subclasses.
   */
  registerForEditor(): void {
    if (!this.pluginName) {
      throw new Error(`${this.constructor.name} must define a static pluginName property.`);
    }
    if (!PLUGIN_REGISTRY_INSTANCE.hasPlugin(this.pluginName)) {
      PLUGIN_REGISTRY_INSTANCE.registerPluginAndItsExtensions(this);
    } else {
      warn(`Plugin ${this.pluginName} is already registered. Skipping registration.`);
    }
  }

  /**
   * Removes this plugin from the registry and clears its adapters/validator from the editor.
   * Intended for test teardown only; do not use in production.
   */
  deregisterFromEditor(): void {
    if (PLUGIN_REGISTRY_INSTANCE.hasPlugin(this.pluginName)) {
      PLUGIN_REGISTRY_INSTANCE.deregisterPluginAndItsExtensions(this);
    } else {
      warn(`Plugin ${this.pluginName} is not registered. Skipping deregistration.`);
    }
  }
}
